import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Checkbox,
  Label,
  RadioGroup,
  RadioGroupItem,
  Spinner,
  Textarea,
} from '@agion/ui'
import { History, RefreshCw, SlidersHorizontal } from 'lucide-react'
import { snakeToCamel } from '@/core/infra_utils'
import type { RolloutControlProps } from './rollout_catalog'
import { isRolloutOutcomeUncertain, isRolloutRevisionConflict, RolloutPageError, rolloutErrorText } from './rollout_refusal'
import { SettingsRangeEditor } from './SettingsRangeFields'
import { draftsFromRanges, rangeChanges, valueText, type RangeDrafts } from './settings_range_draft'
import {
  changeSettingsRolloutPolicy,
  getSettingsRolloutPolicy,
  listSettingsRolloutHistory,
  type SettingRange,
  type SettingsRolloutHistoryEntry,
  type SettingsRolloutLevel,
  type SettingsRolloutPolicy,
} from './infra_settings_rollout.service'

const settingsLevels: readonly SettingsRolloutLevel[] = ['off', 'on']
const historyDateOptions: Intl.DateTimeFormatOptions = { dateStyle: 'medium', timeStyle: 'short' }

function settingsLevelControlID(level: SettingsRolloutLevel): string {
  return `settings-rollout-${level}`
}

/**
 * The settings card (PLAN-20261013 slice 08d, agion/Agion#3155). Staff turn
 * on an organisation's own settings and set the range of each live setting;
 * the organisation's owners and admins then write values inside that range
 * from their own settings screen. Every read and write goes through the staff
 * route, also for the active organisation, so only a verified platform
 * administrator reaches it. The server validates each range and audits each
 * change; this card shows what it answers.
 */
export function SettingsRolloutCard({ organization }: RolloutControlProps) {
  const { t, i18n } = useTranslation('pages')
  const cardOrganizationRef = useRef(organization.id)
  const loadGeneration = useRef(0)
  const mutationGeneration = useRef(0)
  cardOrganizationRef.current = organization.id
  const [policy, setPolicy] = useState<SettingsRolloutPolicy | null>(null)
  const [history, setHistory] = useState<SettingsRolloutHistoryEntry[]>([])
  const [targetLevel, setTargetLevel] = useState<SettingsRolloutLevel>('off')
  const [drafts, setDrafts] = useState<RangeDrafts>({})
  const [reason, setReason] = useState('')
  const [confirmed, setConfirmed] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [historyWarning, setHistoryWarning] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  const isCurrent = useCallback(
    (generation: number, counter: { current: number }, organizationId: string) =>
      generation === counter.current && cardOrganizationRef.current === organizationId,
    [],
  )

  const showPolicy = useCallback((current: SettingsRolloutPolicy) => {
    setPolicy(current)
    setTargetLevel(current.level)
    setDrafts(draftsFromRanges(current.ranges))
    setReason('')
    setConfirmed(false)
  }, [])

  const refreshHistory = useCallback(async (generation: number, counter: { current: number }, organizationId: string) => {
    try {
      const currentHistory = await listSettingsRolloutHistory(organizationId)
      if (!isCurrent(generation, counter, organizationId)) return
      setHistory(currentHistory)
    } catch {
      if (!isCurrent(generation, counter, organizationId)) return
      setHistoryWarning(t('rolloutControls.settings.historyRefreshError'))
    }
  }, [isCurrent, t])

  const load = useCallback(async () => {
    const generation = ++loadGeneration.current
    const requestedOrganizationId = organization.id
    setIsLoading(true)
    setError(null)
    setHistoryWarning(null)
    setSaved(false)
    setPolicy(null)
    setHistory([])
    try {
      const current = await getSettingsRolloutPolicy(requestedOrganizationId)
      if (!isCurrent(generation, loadGeneration, requestedOrganizationId)) return
      if (current.organizationId !== requestedOrganizationId) {
        throw new RolloutPageError(t('rolloutControls.target.organizationMismatch'))
      }
      showPolicy(current)
      await refreshHistory(generation, loadGeneration, requestedOrganizationId)
    } catch (loadError) {
      if (!isCurrent(generation, loadGeneration, requestedOrganizationId)) return
      setError(rolloutErrorText(loadError, t, 'rolloutControls.loadError'))
    } finally {
      if (isCurrent(generation, loadGeneration, requestedOrganizationId)) setIsLoading(false)
    }
  }, [isCurrent, organization.id, refreshHistory, showPolicy, t])

  const invalidatePendingRequests = useCallback(() => {
    loadGeneration.current++
    mutationGeneration.current++
  }, [])

  useEffect(() => {
    mutationGeneration.current++
    setIsSaving(false)
    void load()
    return invalidatePendingRequests
  }, [invalidatePendingRequests, load])

  const pending = useMemo(() => rangeChanges(policy?.ranges ?? [], drafts), [policy, drafts])
  const changedRangeCount = Object.keys(pending.changes).length
  const reasonBytes = useMemo(() => new TextEncoder().encode(reason.trim()).length, [reason])
  const hasChange = policy !== null && (targetLevel !== policy.level || changedRangeCount > 0)
  const canApply = hasChange
    && !pending.invalid
    && reasonBytes > 0
    && reasonBytes <= 500
    && confirmed
    && !isSaving

  const apply = async () => {
    if (!policy || !canApply) return
    const generation = ++mutationGeneration.current
    const requestedOrganizationId = organization.id
    setIsSaving(true)
    setError(null)
    setHistoryWarning(null)
    setSaved(false)
    try {
      const updated = await changeSettingsRolloutPolicy(requestedOrganizationId, {
        expectedRevision: policy.revision,
        level: targetLevel,
        ...(changedRangeCount > 0 ? { ranges: pending.changes } : {}),
        reason: reason.trim(),
        confirmed: true,
      })
      if (!isCurrent(generation, mutationGeneration, requestedOrganizationId)) return
      if (updated.organizationId !== requestedOrganizationId) {
        throw new RolloutPageError(t('rolloutControls.target.organizationMismatch'))
      }
      showPolicy(updated)
      setSaved(true)
      await refreshHistory(generation, mutationGeneration, requestedOrganizationId)
    } catch (saveError) {
      if (!isCurrent(generation, mutationGeneration, requestedOrganizationId)) return
      if (isRolloutRevisionConflict(saveError) || isRolloutOutcomeUncertain(saveError)) {
        await load()
        if (isCurrent(generation, mutationGeneration, requestedOrganizationId)) {
          setError(isRolloutRevisionConflict(saveError)
            ? t('rolloutControls.settings.conflict')
            : rolloutErrorText(saveError, t, 'rolloutControls.settings.saveError'))
        }
      } else {
        setError(rolloutErrorText(saveError, t, 'rolloutControls.settings.saveError'))
      }
    } finally {
      if (isCurrent(generation, mutationGeneration, requestedOrganizationId)) setIsSaving(false)
    }
  }

  const unlimitedText = t('rolloutControls.settings.unlimited')
  const rangeName = (key: string) => policy?.ranges.find((range) => snakeToCamel(range.key) === key)
  const historyRange = (key: string, range: SettingRange | undefined) => {
    const shown = rangeName(key)
    if (!range) return t('rolloutControls.settings.usesDefault')
    const unit = shown?.unit ?? ''
    return t('rolloutControls.settings.rangeText', {
      minimum: valueText(range.minimum, unit, unlimitedText),
      maximum: valueText(range.maximum, unit, unlimitedText),
    })
  }

  return (
    <Card>
      <CardHeader className="border-b border-slate-100 pb-4 dark:border-slate-800">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="rounded-lg bg-sky-50 p-2 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300">
              <SlidersHorizontal className="h-5 w-5" aria-hidden="true" />
            </div>
            <div>
              <CardTitle>{t('rolloutControls.settings.title')}</CardTitle>
              <CardDescription className="mt-1">{t('rolloutControls.settings.description')}</CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="purple">{t('rolloutControls.settings.organizationScoped')}</Badge>
            {policy && (
              <Badge variant={policy.level === 'on' ? 'success' : 'default'}>
                {t(`rolloutControls.settings.levels.${policy.level}.label`)}
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-5">
        {isLoading ? (
          <div className="flex items-center justify-center py-12"><Spinner /></div>
        ) : error && !policy ? (
          <Alert variant="error">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span>{error}</span>
              <Button variant="secondary" size="sm" onClick={() => void load()}>
                <RefreshCw className="mr-1 h-4 w-4" />{t('rolloutControls.refresh')}
              </Button>
            </div>
          </Alert>
        ) : policy ? (
          <>
            {error && <Alert variant="error">{error}</Alert>}
            {saved && <Alert variant="success">{t('rolloutControls.settings.saved')}</Alert>}
            {historyWarning && <Alert variant="warning">{historyWarning}</Alert>}
            <Alert variant="info">{t('rolloutControls.settings.rangeNotice')}</Alert>

            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(18rem,0.7fr)]">
              <div>
                <div className="mb-2 text-sm font-medium text-slate-800 dark:text-slate-200">
                  {t('rolloutControls.settings.targetLevel')}
                </div>
                <RadioGroup
                  value={targetLevel}
                  onValueChange={(value) => {
                    setTargetLevel(value as SettingsRolloutLevel)
                    setConfirmed(false)
                    setSaved(false)
                  }}
                  className="space-y-2"
                >
                  {settingsLevels.map((level) => {
                    const id = settingsLevelControlID(level)
                    return (
                      <div
                        key={level}
                        className={`flex items-start gap-3 rounded-lg border p-3 transition-colors ${targetLevel === level
                          ? 'border-sky-400 bg-sky-50/70 dark:border-sky-600 dark:bg-sky-950/20'
                          : 'border-slate-200 dark:border-slate-700'}`}
                      >
                        <RadioGroupItem
                          id={id}
                          value={level}
                          aria-label={t(`rolloutControls.settings.levels.${level}.label`)}
                          className="mt-0.5"
                        />
                        <Label htmlFor={id} className="min-w-0 flex-1 cursor-pointer">
                          <span className="block text-sm font-medium text-slate-900 dark:text-slate-100">
                            {t(`rolloutControls.settings.levels.${level}.label`)}
                          </span>
                          <span className="mt-1 block text-xs leading-5 text-slate-500 dark:text-slate-400">
                            {t(`rolloutControls.settings.levels.${level}.description`)}
                          </span>
                        </Label>
                      </div>
                    )
                  })}
                </RadioGroup>
              </div>

              <div className="rounded-lg border border-slate-200 p-4 dark:border-slate-700">
                <dl className="space-y-3 text-sm">
                  <div>
                    <dt className="text-xs uppercase tracking-wide text-slate-500">{t('rolloutControls.settings.currentOrganization')}</dt>
                    <dd className="mt-1 font-medium text-slate-900 dark:text-slate-100">{organization.name}</dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase tracking-wide text-slate-500">{t('rolloutControls.settings.currentLevel')}</dt>
                    <dd className="mt-1">{t(`rolloutControls.settings.levels.${policy.level}.label`)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase tracking-wide text-slate-500">{t('rolloutControls.settings.revision')}</dt>
                    <dd className="mt-1 font-mono">{policy.revision}</dd>
                  </div>
                </dl>
              </div>
            </div>

            <div className="space-y-3">
              <div className="text-sm font-medium text-slate-800 dark:text-slate-200">{t('rolloutControls.settings.rangesTitle')}</div>
              {policy.ranges.length === 0 ? (
                <p className="text-sm text-slate-500 dark:text-slate-400">{t('rolloutControls.settings.noRanges')}</p>
              ) : policy.ranges.map((range) => {
                const draft = drafts[range.key]
                return draft ? (
                  <SettingsRangeEditor
                    key={range.key}
                    range={range}
                    draft={draft}
                    disabled={isSaving}
                    onChange={(next) => {
                      setDrafts((current) => ({ ...current, [range.key]: next }))
                      setConfirmed(false)
                      setSaved(false)
                    }}
                  />
                ) : null
              })}
            </div>

            <div>
              <Textarea
                id="settings-rollout-reason"
                label={t('rolloutControls.settings.reasonLabel')}
                placeholder={t('rolloutControls.settings.reasonPlaceholder')}
                value={reason}
                rows={3}
                onChange={(event) => {
                  setReason(event.target.value.replace(/[\r\n\t]+/g, ' '))
                  setSaved(false)
                }}
                error={reasonBytes > 500 ? t('rolloutControls.settings.reasonTooLong') : undefined}
              />
              <div className="mt-1 text-right text-xs text-slate-400">
                {t('rolloutControls.settings.reasonBytes', { bytes: reasonBytes })}
              </div>
            </div>

            {hasChange && (
              <Checkbox
                id="settings-rollout-confirmation"
                checked={confirmed}
                onCheckedChange={(checked) => setConfirmed(checked === true)}
                label={t('rolloutControls.settings.confirmation', {
                  organization: organization.name,
                  level: t(`rolloutControls.settings.levels.${targetLevel}.label`),
                  ranges: changedRangeCount,
                })}
              />
            )}

            <div className="rounded-lg border border-dashed border-slate-300 p-4 dark:border-slate-700">
              <div className="text-sm font-medium text-slate-800 dark:text-slate-200">{t('rolloutControls.settings.retirementTitle')}</div>
              <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">{t('rolloutControls.settings.retirementDescription')}</p>
            </div>

            <div>
              <div className="mb-3 flex items-center gap-2 text-sm font-medium text-slate-800 dark:text-slate-200">
                <History className="h-4 w-4" aria-hidden="true" />
                {t('rolloutControls.settings.historyTitle')}
              </div>
              {history.length === 0 ? (
                <p className="text-sm text-slate-500 dark:text-slate-400">{t('rolloutControls.settings.noHistory')}</p>
              ) : (
                <ol className="divide-y divide-slate-100 rounded-lg border border-slate-200 dark:divide-slate-800 dark:border-slate-700">
                  {history.map((entry) => {
                    const rangeKeys = Array.from(new Set([
                      ...Object.keys(entry.previousRanges ?? {}),
                      ...Object.keys(entry.ranges ?? {}),
                    ])).filter((key) => JSON.stringify(entry.previousRanges?.[key]) !== JSON.stringify(entry.ranges?.[key]))
                    return (
                      <li key={entry.revision} className="p-3 text-sm">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <span className="font-medium text-slate-800 dark:text-slate-200">
                            {t('rolloutControls.settings.historyTransition', {
                              from: t(`rolloutControls.settings.levels.${entry.previousLevel}.label`),
                              to: t(`rolloutControls.settings.levels.${entry.level}.label`),
                            })}
                          </span>
                          <time className="text-xs text-slate-400" dateTime={entry.changedAt}>
                            {new Intl.DateTimeFormat(i18n.language, historyDateOptions).format(new Date(entry.changedAt))}
                          </time>
                        </div>
                        {rangeKeys.map((key) => (
                          <p key={key} className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                            {t('rolloutControls.settings.historyRange', {
                              name: rangeName(key)?.name ?? key,
                              from: historyRange(key, entry.previousRanges?.[key]),
                              to: historyRange(key, entry.ranges?.[key]),
                            })}
                          </p>
                        ))}
                        <p className="mt-1 text-slate-600 dark:text-slate-400">{entry.reason}</p>
                        <div className="mt-1 font-mono text-[11px] text-slate-400">
                          {t('rolloutControls.settings.historyEvidence', { actor: entry.actorId, revision: entry.revision })}
                        </div>
                      </li>
                    )
                  })}
                </ol>
              )}
            </div>
          </>
        ) : null}
      </CardContent>

      {policy && !isLoading && (
        <CardFooter className="justify-end border-t border-slate-100 pt-4 dark:border-slate-800">
          <Button variant="primary" disabled={!canApply} isLoading={isSaving} onClick={() => void apply()}>
            {t('rolloutControls.settings.apply')}
          </Button>
        </CardFooter>
      )}
    </Card>
  )
}
